<template>
    <div class="component-container">
        <b-card class="diagram-card">
            <div class="d-flex flex-column pb-2">
                <h4 class="text-left font-weight-bold">{{ title }}</h4>
                <h6 class="text-left ml-1">{{ subtitle }}</h6>
            </div>
            <div class="chart-container">
                <div class="chart">
                    <LineChartGenerator :chart-options="chartOptions" :chart-data="availableChartData" :chart-id="chartId" :dataset-id-key="datasetIdKey" class="line-chart" />
                </div>
            </div>
            <p class="diagram-footer"><i>*Reach (000) A3-99 (1min & 15s) Total</i></p>
        </b-card>
    </div>
</template>

<script>
import { Line as LineChartGenerator } from "vue-chartjs/legacy"
import { Chart as ChartJS, Title, Tooltip, Legend, LineElement, LinearScale, CategoryScale, PointElement } from "chart.js"
ChartJS.register(Title, Tooltip, Legend, LineElement, LinearScale, CategoryScale, PointElement)

export default {
    name: "OverviewDiagram",
    components: {
        LineChartGenerator,
    },
    props: {
        chartId: {
            type: String,
            default: "line-chart",
        },
        datasetIdKey: {
            type: String,
            default: "label",
        },
        title: {
            type: String,
            default: "",
        },
        subtitle: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            regexRemoveHtml: /<[^>]*>|<\/[^>]*>/gm,
            availableChartData: { labels: [], datasets: [] },
            visibleChartData: { labels: [], datasets: [] },
            visibleProviders: [],
            visibleMeasures: [],
            predefinedChannelColors: {
                Discovery: ["#4472c4", "#83A2D8", "#44B2C4"],
                SVT: ["#ed8036", "#F4AC7C", "#CE5D12"],
                TV4: ["#989898", "#cbcbcb", "#545454"],
                "Viaplay Group": ["#ffc515", "#FFD65C", "#45FD02"],
            },
            chartOptions: {
                locale: "sv",
                maintainAspectRatio: false,
                hover: {
                    mode: "dataset",
                },
                scales: {
                    y: {
                        beginAtZero: false,
                        grid: {
                            display: true,
                            //show horizontal gridline only for every other tick
                            color: function(context) {
                                return context.index % 2 == 0 ? "#E5E5E5" : "transparent"
                            },
                        },
                        ticks: {
                            //needed to remove thousand divider " "
                            callback: function(value) {
                                return value
                            },
                        },
                    },
                    x: {
                        grid: {
                            display: false,
                        },
                    },
                },
                plugins: {
                    legend: {
                        display: true,
                        position: "right",
                        align: "center",
                        title: {
                            display: true,
                            padding: -25,
                        },
                        labels: {
                            padding: 25,
                            boxHeight: 10,
                            boxWidth: 40,
                            background: "white",
                        },
                        onHover: this.handleHover,
                        onLeave: this.handleLeave,
                    },
                    tooltip: {
                        callbacks: {
                            label: function(context) {
                                let label = context.dataset.label || ""

                                if (label) {
                                    label += ": "
                                }
                                if (context.parsed.y !== null) {
                                    label += new Intl.NumberFormat("sv", { useGrouping: false }).format(context.parsed.y)
                                }
                                return label
                            },
                        },
                        animation: {
                            numbers: { duration: 0 },
                            colors: {
                                type: "color",
                                duration: 100,
                                from: "transparent",
                            },
                        },
                    },
                    // required to avoid memory stack full error
                    annotation: {
                        annotations: [],
                    },
                },
                datasets: {
                    line: {
                        //common settings for all lines
                        borderWidth: 3,
                        hoverBorderWidth: 5,
                        pointHitRadius: 10,
                    },
                },
            },
        }
    },
    computed: {
        levelOneSitesWithLevelTwoSites() {
            return this.$store.getters.getSitesReachSevenDaysResults.data.levelOneSitesWithLevelTwoSites.map(item => {
                return {
                    ...item,
                    providerName: item.providerName.replace(this.regexRemoveHtml, ""),
                }
            })
        },
        fields() {
            let fields = [...this.$store.getters.getSitesReachSevenDaysResults.data.columns]
            fields.unshift({ key: "plussign", tdClass: "plusminussigntd", thClass: "plusminussignth", label: "" }, { key: "chosen", thClass: "choose-checkbox-th", label: "Valda" })
            return fields
        },
        getMeasuresKeyLabelPairs() {
            let fields = [...this.$store.getters.getSitesReachSevenDaysResults.data.columns]
            let newFields = Object.values(fields).reduce((all, m) => {
                if (m.key.includes("measure")) {
                    all[m.key] = `Sum of ${m.label}`
                    return all
                }
                return all
            }, [])
            return newFields
        },
        getDates() {
            let items = this.$store.getters.getSitesReachSevenDaysResults.data.levelOneSitesWithLevelTwoSites
            return [...new Set(items.map(i => i.date))]
        },
        getProviders() {
            let items = this.$store.getters.getSitesReachSevenDaysResults.data.levelOneSitesWithLevelTwoSites
            //remove html from providerName
            return [...new Set(items.filter(p => p.isParent).map(i => i.providerName.replace(this.regexRemoveHtml, "")))]
        },
    },
    watch: {
        "$store.getters.getSitesReachSevenDaysResults.status": {
            immediate: true,
            handler(newValue) {
                if (newValue != "NOT_LOADED") {
                    this.setAvailableChartData(this.getDates, this.getProviders, this.getMeasuresKeyLabelPairs)
                }
            },
        },
    },
    methods: {
        setAvailableChartData(dates, providers, measures) {
            //isParent has measure sum for channel at date
            let items = this.$store.getters.getSitesReachSevenDaysResults.data.levelOneSitesWithLevelTwoSites.filter(p => p.isParent)
            //remove html from providerName
            items = items.map(item => {
                return {
                    ...item,
                    providerName: item.providerName.replace(this.regexRemoveHtml, ""),
                }
            })
            let chartDataSets = []
            for (let indexP = 0; indexP < providers.length; indexP++) {
                let providerItems = items.filter(p => p.providerName == providers[indexP])
                let measureData = []
                let measureKeys = Object.keys(measures)
                let color
                for (let indexM = 0; indexM < measureKeys.length; indexM++) {
                    let providerDate = []
                    for (let indexD = 0; indexD < dates.length; indexD++) {
                        providerDate.push(providerItems.filter(d => d.date == dates[indexD]))
                    }
                    measureData = providerDate.flat().map(d => {
                        return parseFloat(d[measureKeys[indexM]].replace(/,/g, ".")).toFixed(1)
                    })
                    //half of max measures get a new color
                    if (indexM >= this.maxMeasures / 2) {
                        color = this.predefinedChannelColors[providers[indexP]] && this.predefinedChannelColors[providers[indexP]][1] ? this.predefinedChannelColors[providers[indexP]][1] : "black"
                    } else {
                        color = this.predefinedChannelColors[providers[indexP]] && this.predefinedChannelColors[providers[indexP]][0] ? this.predefinedChannelColors[providers[indexP]][0] : "black"
                    }
                    chartDataSets.push({
                        label: `${providers[indexP]}`,
                        data: measureData,
                        borderColor: color,
                        backgroundColor: color,
                        metadata: { provider: providers[indexP], measure: measures[measureKeys[indexM]] },
                    })
                }
            }
            this.availableChartData = {
                labels: this.getDates,
                datasets: chartDataSets,
            }
        },
        handleHover(evt, item, legend) {
            legend.chart.data.datasets.forEach((ds, index) => {
                ds.borderColor = index === item.datasetIndex || ds.borderColor.length === 9 ? ds.borderColor : ds.borderColor + "4D"
            })
            legend.chart.update()
        },
        handleLeave(evt, item, legend) {
            legend.chart.data.datasets.forEach(ds => {
                ds.borderColor = ds.borderColor.length === 9 ? ds.borderColor.slice(0, -2) : ds.borderColor
            })
            legend.chart.update()
        },
    },
}
</script>

<style scoped>
.component-container {
    display: flex;
    justify-content: center;
}
.diagram-card {
    min-width: 718px;
    width: 100%;
    /* taken from the intial width of the two toplists */
    max-width: 1518px;
    margin: 5rem 0;
    display: flex;
    flex-direction: column;
    border-radius: 10px;
    border: none;
    border-top: 1px solid #e0e0e0;
    border-right: 1px solid #e0e0e0;
    box-shadow: -0.2px 1px 1.4px rgba(0, 0, 0, 0.09), -0.6px 3px 3.5px rgba(0, 0, 0, 0.067), -1.2px 6px 7.1px rgba(0, 0, 0, 0.061), -2.6px 12px 14.6px rgba(0, 0, 0, 0.054),
        -7px 24px 40px rgba(0, 0, 0, 0.04);
}
.diagram-title {
    text-align: left;
    margin-bottom: 1.2rem;
}
.diagram-footer {
    font-size: 0.8rem;
    text-align: right;
    margin-bottom: -0.7rem;
    color: #989898;
}
.totalRowsStyle {
    display: flex;
    position: relative;
}
.chart-container {
    border: 1px solid #dee2e6;
    display: flex;
    flex-direction: column;
    padding: 1rem;
    padding-top: 0;
    flex-grow: 1;
    height: 30rem;
    width: 100%;
    border: none;
}
.chart-header {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 1rem 2.5rem;
    padding-bottom: 0.5rem;
}
.measure-dropdown {
    width: 100%;
    max-width: 30rem;
}
.provider-dropdown {
    justify-self: end;
    width: 100%;
    max-width: 15rem;
}
.chart {
    flex-grow: 1;
    overflow: auto;
    position: relative;
    background-color: white;
    overflow: hidden;
}
.line-chart {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
}
</style>
